import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  IonAlert,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  useIonToast,
} from "@ionic/react";
import useBooking from "../../hooks/bookings/useBooking";
import { AuthenticationContext } from "../../providers/AuthenticationProvider/AuthenticationProvider";
import useClubsByCoach from "../../hooks/useClubsByCoach";
import useFieldsByClub from "../../hooks/useFieldsByClub";
import useTagsByCoach from "../../hooks/useTagsByCoach";
import AthletesModal from "../../components/AthletesModal/AthletesModal";
import TagModal from "../../components/TagModal/TagModal";
import TagList from "../../components/TagList/TagList";
import ParticipantList from "../../components/ParticipantList/ParticipantList";
import PageTitle from "../../components/PageTitle/PageTitle";
import { FormattedDate, FormattedTime } from "react-intl";
import useAPI from "../../hooks/basic/useAPI";
import NewMatchModal from "../../components/NewMatchModal/NewMatchModal";
import { computePenaltyPrompt } from "./editBookingPenalty";

const EditBooking = ({ match }) => {
  const parsedBookingId =
    match.params.bookingId !== "add" ? match.params.bookingId : null;
  const [clubId, setClubId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const { appUserId } = useContext(AuthenticationContext);
  const { data, isLoading, error, refreshData } = useBooking(parsedBookingId);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [openNewMatchModal, setOpenNewMatchModal] = useState(false);
  // const { data: athletes } = useAthletesByCoach(appUserId);
  const { data: clubsData } = useClubsByCoach(appUserId);
  const { data: tags } = useTagsByCoach(appUserId);
  const { data: fieldsData } = useFieldsByClub(clubId);
  const [openAthletesModal, setOpenAthletesModal] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [showPenaltyAlert, setShowPenaltyAlert] = useState(false);

  const { callAPI } = useAPI();
  const [present] = useIonToast();
  // `chargePenalty` only matters for participants removed < 24h before a group
  // lesson; the coach is asked about it at save time (see handleUpdateClick),
  // so it is never surfaced as a standalone, always-on toggle.
  const updateBooking = async (chargePenalty = true) => {
    const club = clubsData.find((c) => c.id === clubId);
    const field = fieldsData.find((f) => f.id === fieldId);

    const booking = {
      id: data.id,
      club: `/societa/${clubId}`,
      field: `societa/${clubId}/campo/${fieldId}`,
      participantUserIds: selectedAthletes
        .filter((a) => a.userId)
        .map((a) => a.userId),
      participants: selectedAthletes,
      remainingSlots:
        data.maxParticipants - selectedAthletes.length > 0
          ? data.maxParticipants - selectedAthletes.length
          : 0,
      tags: selectedTags.map((t) => t.id),
      clubName: club.nome,
      fieldName: field.nome,
      lastUpdateUserId: appUserId,
      // Applies to participants removed in this update (24h penalty on/off).
      chargePenalty,
    };
    await callAPI({
      method: "post",
      url: "/update-lesson",
      data: booking,
    });
  };

  const handleUpdate = async (chargePenalty = true) => {
    try {
      await updateBooking(chargePenalty);
      // Re-fetch so the page reflects the saved state instead of requiring a
      // manual reload (the booking doc and synced local state stay aligned).
      refreshData();
      present({
        message: "Evento aggiornato con successo",
        duration: 3000,
        color: "success",
        position: "bottom",
      });
    } catch (err) {
      console.error("Errore nell'aggiornamento dell'evento", err);
      present({
        message: "Errore nell'aggiornamento dell'evento",
        duration: 4000,
        color: "danger",
        position: "bottom",
      });
    }
  };

  const populateAthletes = async () => {
    // let validParticipantsIds = data.participantUserIds;

    // const notFullGroups = await callAPI({
    //   url: "/not-full-groups",
    //   params: {
    //     coachId: appUserId,
    //   },
    //   preventCall: !appUserId,
    // });

    // // merge validParticipantsIds with notFullGroups participants
    // const notFullGroupsParticipantIds = notFullGroups.map(
    //   (group) => group.user_id,
    // );

    // // merge validParticipantsIds with notFullGroupsParticipantIds avoiding duplicates
    // validParticipantsIds = [
    //   ...new Set([...validParticipantsIds, ...notFullGroupsParticipantIds]),
    // ];

    const allAthletes = await callAPI({
      url: "/athletes-by-coach",
      params: {
        coachId: appUserId,
      },
      preventCall: !appUserId,
    });
    // setAthletes(allAthletes.filter((a) => validParticipantsIds.includes(a.id)));
    setAthletes(allAthletes);
  };

  useEffect(() => {
    if (data && tags) {
      const tagsArray =
        tags?.filter((tag) => data.tags?.includes(tag.id)) || [];
      setSelectedTags(tagsArray);
      setSelectedAthletes(data.participants);
      setClubId(data.club.split("/").pop());
      setFieldId(data.field.split("/").pop());
    }
  }, [data, tags]);

  useEffect(() => {
    populateAthletes();
  }, []);

  if (isLoading) {
    return <p>Loading...</p>;
  }
  if (error) {
    return <p>Error: {error.message}</p>;
  }

  const { from, to } = data;
  const canCancelEvent = appUserId && appUserId === data.relatedSupervisorId;

  // Whether (and for whom) to ask the coach about the 24h removal penalty.
  const { lateRemovedNames, askPenaltyOnSave } = computePenaltyPrompt({
    originalParticipantUserIds: data.participantUserIds,
    originalParticipants: data.participants,
    selectedAthletes,
    fromSeconds: from.seconds,
    type: data.type,
  });

  const handleUpdateClick = () => {
    if (askPenaltyOnSave) {
      setShowPenaltyAlert(true);
    } else {
      setShowUpdateAlert(true);
    }
  };
  return (
    <IonPage>
      <IonContent className="ion-ion-padding">
        <PageTitle>Dettaglio evento</PageTitle>
        <IonText>
          <h2>Inizio: </h2>
        </IonText>
        <p>
          <FormattedDate
            value={from.seconds * 1000}
            day="2-digit"
            month="long"
            weekday="short"
            year="numeric"
          />
          &nbsp;-&nbsp;
          <FormattedTime value={from.seconds * 1000} />
        </p>
        <IonText>
          <h2>Fine: </h2>
        </IonText>
        <p>
          <FormattedDate
            value={to.seconds * 1000}
            day="2-digit"
            month="long"
            weekday="short"
            year="numeric"
          />
          &nbsp;-&nbsp;
          <FormattedTime value={to.seconds * 1000} />
        </p>
        <IonList>
          <IonItem>
            <IonSelect
              aria-label="Fruit"
              interface="action-sheet"
              placeholder="Seleziona un club"
              label="Seleziona un club"
              onIonChange={(e) => {
                setClubId(e.detail.value);
                setFieldId(null);
              }}
              value={clubId}
            >
              {clubsData &&
                clubsData.map((club) => (
                  <IonSelectOption key={club.id} value={club.id}>
                    {club.nome}
                  </IonSelectOption>
                ))}
            </IonSelect>
          </IonItem>
          <IonItem>
            <IonSelect
              aria-label="Seleziona un campo"
              interface="action-sheet"
              placeholder="Seleziona un campo"
              label="Seleziona un campo"
              value={fieldId}
              onIonChange={(e) => setFieldId(e.detail.value)}
            >
              {fieldsData &&
                fieldsData.map((field) => (
                  <IonSelectOption key={field.id} value={field.id}>
                    {field.nome}
                  </IonSelectOption>
                ))}
            </IonSelect>
          </IonItem>
          <IonItem onClick={() => setOpenAthletesModal(true)}>
            <IonLabel className="custom-label">Allievi</IonLabel>
            <ParticipantList
              participants={selectedAthletes}
              onRemoveClick={(e, currentParticipant) => {
                e.preventDefault();
                setSelectedAthletes(
                  selectedAthletes.filter(
                    (participant) =>
                      participant.userId !== currentParticipant.userId,
                  ),
                );
              }}
            />
          </IonItem>
          <IonItem onClick={() => setOpenTagModal(true)}>
            <IonLabel className="custom-label">Tag</IonLabel>
            <TagList
              tags={selectedTags}
              onRemoveClick={(e, currentTag) => {
                e.preventDefault();
                setSelectedTags(
                  selectedTags.filter((tag) => tag.id !== currentTag.id),
                );
              }}
            />
          </IonItem>
          {/* <IonItem onClick={() => setOpenTagModal(true)}>
              <IonButton>Open</IonButton>
              {selectedTags.map(({ description }) => description).join(", ")}
            </IonItem> */}
        </IonList>
        {canCancelEvent && (
          <div>
            <IonButton id="present-alert" color="danger">
              Cancella Evento
            </IonButton>
            <IonAlert
              header="Vuoi diminuire il numero totale di lezioni del gruppo?"
              trigger="present-alert"
              buttons={[
                {
                  text: "Annulla",
                  role: "cancel",
                  handler: async () => {},
                },
                {
                  text: "No",
                  role: "confirm",
                  handler: async () => {
                    await callAPI({
                      method: "post",
                      url: "/delete-lesson",
                      data: {
                        lessonId: parsedBookingId,
                        decreaseLessons: false,
                      },
                    });

                    // await deleteBooking(parsedBookingId);
                    // go back to the previous page
                    window.history.back();
                  },
                },
                {
                  text: "Sì",
                  role: "confirm",
                  handler: async () => {
                    await callAPI({
                      method: "post",
                      url: "/delete-lesson",
                      data: {
                        lessonId: parsedBookingId,
                        decreaseLessons: true,
                      },
                    });

                    // await deleteBooking(parsedBookingId);
                    // go back to the previous page
                    window.history.back();
                  },
                },
              ]}
              onDidDismiss={({ detail }) =>
                console.log(`Dismissed with role: ${detail.role}`)
              }
            ></IonAlert>
          </div>
        )}
        <div>
          <IonButton color="primary" onClick={handleUpdateClick}>
            Aggiorna
          </IonButton>
          <IonAlert
            header="Vuoi aggiornare i dati?"
            isOpen={showUpdateAlert}
            onDidDismiss={() => setShowUpdateAlert(false)}
            buttons={[
              {
                text: "Annulla",
                role: "cancel",
              },
              {
                text: "Conferma",
                role: "confirm",
                handler: () => handleUpdate(true),
              },
            ]}
          ></IonAlert>
          <IonAlert
            header="Allievi rimossi a meno di 24 ore"
            message={
              `Stai rimuovendo a meno di 24h dall'evento: ${lateRemovedNames}. ` +
              "Vuoi addebitare la penalità (perdono il diritto al recupero della lezione)?"
            }
            isOpen={showPenaltyAlert}
            onDidDismiss={() => setShowPenaltyAlert(false)}
            buttons={[
              {
                text: "Annulla",
                role: "cancel",
              },
              {
                text: "No, concedi recupero",
                role: "confirm",
                handler: () => handleUpdate(false),
              },
              {
                text: "Sì, addebita penalità",
                role: "confirm",
                handler: () => handleUpdate(true),
              },
            ]}
          ></IonAlert>
          <IonButton onClick={() => setOpenNewMatchModal(true)} color="primary">
            Crea Partita
          </IonButton>
        </div>
        <AthletesModal
          isOpen={openAthletesModal}
          athletes={athletes}
          selectedAthletes={selectedAthletes}
          onConfirm={(athletes) => {
            setSelectedAthletes(athletes);
            setOpenAthletesModal(false);
          }}
          onDismiss={() => setOpenAthletesModal(false)}
        />
        <TagModal
          isOpen={openTagModal}
          tags={tags}
          selectedTags={selectedTags}
          onConfirm={(tags) => {
            setSelectedTags(tags);
            setOpenTagModal(false);
          }}
          onDismiss={() => setOpenTagModal(false)}
          onModalDismiss={() => setOpenTagModal(false)}
        />
        {openNewMatchModal && (
          <NewMatchModal
            isOpen={openNewMatchModal}
            athletes={selectedAthletes}
            onDidDismiss={() => setOpenNewMatchModal(false)}
            booking={data}
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default EditBooking;

EditBooking.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      bookingId: PropTypes.string,
    }),
  }),
};
